<template>
    <div class="flex flex-col py-10 mt-4">
        <!-- 가로 스크롤 컨테이너 (PC & Mobile) -->
        <div class="new_lg:mx-auto max-w-full new_lg:max-w-5xl">
            <div
                ref="scrollContainer"
                @scroll="updateScrollIndicator"
                class="flex gap-4 new_lg:gap-8 overflow-x-auto scrollbar-hide px-5 new_lg:px-0"
                style="scroll-behavior: smooth;"
            >
                <div
                    v-for="(item, index) in portfolioItems"
                    :key="index"
                    class="flex-shrink-0 w-[300px] new_lg:w-[calc(50%-1rem)] select-none"
                    :class="[item.url ? 'cursor-pointer' : '']"
                    @click="item.url ? goTo(item.url) : ''"
                >
                    <img
                        :src="item.image"
                        :alt="`image-${index + 1}`"
                        class="w-full pointer-events-none new_lg:rounded-[32px] rounded-[16px]"
                        style="aspect-ratio: 515 / 325;"
                        draggable="false"
                    >
                    <p class="font-basic text-xl font-medium text-text-0 mt-4">{{ item.title }}</p>
                    <p class="font-basic text-base font-normal text-text-1 line-clamp-1 break-all mt-3">
                        {{ item.description }}
                    </p>
                </div>
            </div>

            <!-- 스크롤 인디케이터 -->
            <div class="new_lg:mt-14 mt-10 w-full flex items-center justify-center gap-6 new_lg:gap-4">
                <!-- 왼쪽 화살표 -->
                <button
                    @click="scrollToPrev"
                    class="w-6 h-6 rounded-full border border-gray_2 flex items-center justify-center hover:bg-gray_3 transition-colors flex-shrink-0"
                    :disabled="currentIndex === 0"
                    :class="{ 'opacity-50 cursor-not-allowed': currentIndex === 0 }"
                >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.5 9L4.5 6L7.5 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <!-- 인디케이터 바 -->
                <div class="w-[200px] lg:w-[300px]">
                    <div class="h-1 bg-gray_3 rounded-full relative overflow-hidden">
                        <div
                            ref="scrollIndicator"
                            class="h-full bg-black rounded-full transition-all duration-150 ease-out"
                            :style="{ width: indicatorWidth + '%', transform: `translateX(${indicatorPosition}px)` }"
                        ></div>
                    </div>
                </div>

                <!-- 오른쪽 화살표 -->
                <button
                    @click="scrollToNext"
                    class="w-6 h-6 rounded-full border border-gray_2 flex items-center justify-center hover:bg-gray_3 transition-colors flex-shrink-0"
                    :disabled="currentIndex >= portfolioItems.length - visibleItems"
                    :class="{ 'opacity-50 cursor-not-allowed': currentIndex >= portfolioItems.length - visibleItems }"
                >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PortfolioCarousel',
    props: {
        portfolioItems: {
            type: Array,
            default: () => [
                {
                    image: require('@/assets/images/sw/sw_reference_01.jpg'),
                    title: '큐밋',
                    description: '국내 최초 QA 아웃소싱 & 솔루션 매칭 플랫폼, 큐밋',
                    url: 'https://www.q-meet.com',
                },
                {
                    image: require('@/assets/images/sw/sw_reference_02.jpg'),
                    title: '겟아이즈',
                    description: '소중한 사람에게 선물할때는, 겟아이즈!'
                },
                {
                    image: require('@/assets/images/sw/sw_reference_03.jpg'),
                    title: 'G사',
                    description: '레미콘 운송 통합 관리 구축 프로젝트'
                },
                
            ]
        }
    },
    data() {
        return {
            indicatorWidth: 33.33,
            indicatorPosition: 0,
            currentIndex: 0,
            visibleItems: 1,
        }
    },
    mounted() {
        this.calculateVisibleItems();
        this.calculateIndicatorWidth();
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        goTo(url) {
            window.open(url, '_blank');
        } ,
        handleResize() {
            this.calculateVisibleItems();
            this.calculateIndicatorWidth();
            this.updateScrollIndicator();
        },
        calculateVisibleItems() {
            const isDesktop = window.innerWidth >= 1024; // new_lg breakpoint
            this.visibleItems = isDesktop ? 2 : 1;
        },
        calculateIndicatorWidth() {
            const container = this.$refs.scrollContainer;
            if (!container) return;

            // 실제 보이는 영역의 너비와 전체 스크롤 가능한 영역의 비율로 계산
            const visibleWidth = container.clientWidth;
            const totalWidth = container.scrollWidth;

            // indicator의 너비를 백분율로 계산
            this.indicatorWidth = (visibleWidth / totalWidth) * 100;
        },
        updateScrollIndicator() {
            const container = this.$refs.scrollContainer;
            if (!container) return;

            const scrollLeft = container.scrollLeft;
            const scrollWidth = container.scrollWidth - container.clientWidth;

            // 스크롤이 없는 경우 (모든 아이템이 보이는 경우)
            if (scrollWidth <= 0) {
                this.indicatorPosition = 0;
                return;
            }

            const scrollPercentage = scrollLeft / scrollWidth;

            // 인디케이터 바의 이동 위치 계산
            const isDesktop = window.innerWidth >= 1024;
            const indicatorBarWidth = isDesktop ? 300 : 200; // PC: 300px, Mobile: 200px
            const maxPosition = indicatorBarWidth * (1 - this.indicatorWidth / 100);

            this.indicatorPosition = scrollPercentage * maxPosition;

            // 현재 인덱스 업데이트
            this.updateCurrentIndex();
        },
        updateCurrentIndex() {
            const container = this.$refs.scrollContainer;
            if (!container) return;

            const scrollLeft = container.scrollLeft;
            const itemWidth = container.children[0]?.offsetWidth || 0;
            const gap = this.visibleItems === 2 ? 32 : 16; // new_lg:gap-8 (32px) : gap-4 (16px)

            this.currentIndex = Math.round(scrollLeft / (itemWidth + gap));
        },
        scrollToNext() {
            const container = this.$refs.scrollContainer;
            if (!container) return;

            const nextIndex = Math.min(this.currentIndex + 1, this.portfolioItems.length - this.visibleItems);

            // 마지막 인덱스인 경우 스크롤을 끝으로 이동
            if (nextIndex >= this.portfolioItems.length - this.visibleItems) {
                container.scrollTo({
                    left: container.scrollWidth - container.clientWidth,
                    behavior: 'smooth'
                });
                this.currentIndex = nextIndex;
            } else {
                this.scrollToIndex(nextIndex);
            }
        },
        scrollToPrev() {
            const container = this.$refs.scrollContainer;
            if (!container) return;

            const prevIndex = Math.max(this.currentIndex - 1, 0);

            // 첫 번째 인덱스인 경우 스크롤을 처음으로 이동
            if (prevIndex === 0) {
                container.scrollTo({
                    left: 0,
                    behavior: 'smooth'
                });
                this.currentIndex = 0;
            } else {
                this.scrollToIndex(prevIndex);
            }
        },
        scrollToIndex(index) {
            const container = this.$refs.scrollContainer;
            if (!container || !container.children[index]) return;

            const targetElement = container.children[index];
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
            });

            this.currentIndex = index;
        },
    },
}
</script>

<style scoped>
/* 스크롤바 숨기기 */
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
